import React from "react";
import { Upload, FileSpreadsheet, Check, Search, MapPin, Users } from "lucide-react";
import { cn } from "@/lib/utils";

const ICONS = {
  upload: Upload,
  map: MapPin,
  dup: Users,
  preview: Search,
  done: Check,
};

/**
 * ImportStepper — premium 5-step horizontal indicator.
 * Visually shows: Fichier → Mapping → Doublons → Aperçu → Importer.
 * Active step is filled with primary, completed steps show a check.
 */
export default function ImportStepper({ steps, current }) {
  return (
    <div
      className="flex items-start justify-between gap-1 md:gap-2 overflow-x-auto scrollbar-hide"
      data-testid="import-stepper"
    >
      {steps.map((s, idx) => {
        const Icon = ICONS[s.icon] || Upload;
        const isActive = idx === current;
        const isDone = idx < current;
        return (
          <React.Fragment key={s.key}>
            <div
              className="flex flex-col items-center text-center min-w-[64px] md:min-w-[88px]"
              data-testid={`step-${s.key}`}
              data-active={isActive || undefined}
              data-done={isDone || undefined}
            >
              <div
                className={cn(
                  "relative h-10 w-10 md:h-11 md:w-11 rounded-2xl border flex items-center justify-center transition-all duration-200",
                  isActive && "border-primary bg-primary/10 text-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.1)]",
                  isDone && "border-success/40 bg-success/15 text-success",
                  !isActive && !isDone && "border-border dark:border-border bg-card dark:bg-surface text-muted-foreground dark:text-muted-foreground/70"
                )}
              >
                {isDone ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                <span
                  className={cn(
                    "absolute -bottom-1 -right-1 text-[9px] tabular-nums font-semibold rounded-full h-4 w-4 flex items-center justify-center border",
                    isActive && "bg-primary text-primary-foreground border-primary",
                    isDone && "bg-success/30 text-success border-success/40",
                    !isActive && !isDone && "bg-background text-muted-foreground/80 dark:text-muted-foreground/60 border-border dark:border-border"
                  )}
                >
                  {idx + 1}
                </span>
              </div>
              <div
                className={cn(
                  "mt-2 text-[11px] md:text-xs font-medium leading-tight",
                  isActive ? "text-foreground" : "text-muted-foreground"
                )}
              >
                {s.label}
              </div>
              <div className="hidden md:block text-[10px] text-muted-foreground/80 dark:text-muted-foreground/60 leading-tight mt-0.5">
                {s.sub}
              </div>
            </div>
            {idx < steps.length - 1 && (
              <div className="flex-1 h-px mt-5 md:mt-6 bg-gradient-to-r from-border via-border/60 to-border dark:from-white/10 dark:via-white/5 dark:to-white/10 min-w-[16px]" aria-hidden />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
